import { getQoeScore, getAvailabilityScore } from "./common";

const defaultColor = "var(--hbr-color-neutral-text, #23282E)";

export const colors = {
  amber60: "#CC8604", // warning sentiment
  amber55: "#BD7202",
  yellow85: "#F2D268",
  yellow70: "#E0A419",
  yellow45: "#A65503",
  blue70: "#7CADF7",
  blue50: "#2774D9", // info sentiment
  blue45: "#1D69CC", // interact sentiment
  //cyan60: "#04A4B0",
  cyan40: "#006773",
  gray100: "#FFFFFF", // white
  gray90: "#E1E4E8",
  gray85: "#D0D4D9",
  gray80: "#C1C6CC",
  gray70: "#a7adb5",
  gray60: "#889099", // neutral sentiment
  gray50: "#6f7680",
  gray45: "#656C75",
  gray40: "#596069",
  gray35: "#464c54",
  gray15: "#23282E", // neutral text
  gray0: "#000000", // black
  green55: "#45991F", // success sentiment
  green50: "#398519",
  green70: "#a1db84",
  green85: "#d1f5bf",
  indigo60: "#7D8AFF",
  lavendar35: "#3B47B2",
  lavendar40: "#545C8A",
  lavendar95: "#EBEDFF",
  lavendar60: "#7D8AFF",
  orange40: "#AD3907",
  purple40: "#753BCC",
  red50: "#D93843",
  red45: "#CC2D37", // danger sentiment
  red70: "#f0b3aa",
  red60: "#FA5762",
  //rose60: "#F2638C",
  rose40: "#B02863",
  grayStrongBase: "#373c42",
  teal60: "#04A4B0",
  //limegreen60: "#7DA11B",
  silver: "#CCC",
  lavender45: "#505ED9",
  purple60: "#A974F7",
  pink45: "#C2306F",
  //lime60: "#6BA32A",
  lime60: "#7DA11B",
  purple45: "#864AE0",
  //pink60: "#E85FC6",
  pink60: "#F2638C",
  teal45: "#017580",
  blue60: "#139BEB",
  slate40: "#545C8A",
  orange50: "#F26722",
  emerald45: "#0B7B46", 
  greenSuccess: "var(--hbr-color-success, #45991F)",
  yellowFair: "var(--hbr-color-warning-border, #cc8604)",
  redPoor: "var(--hbr-color-danger, #CC2D37)",
  grayUnknown: "var(--hbr-color-neutral-border, #889099)",
};

export const textStatusColors = {
  good: colors.green50,
  fair: colors.yellow45,
  poor: colors.redPoor,
  notAvailable: colors.grayUnknown,
  gray: colors.grayUnknown,
  unknown: colors.grayUnknown,
};

export const textChangeColors = {
  positive: textStatusColors.good,
  negative: textStatusColors.poor,
};
  
// New magnetic color sequence list https://www.figma.com/design/03chsH5sSKnIKj3W3YVcWo/%F0%9F%93%96-Magnetic-Component-Guidelines?node-id=69988-91239&p=f&m=dev
// https://magnetic.cisco.com/0a43ab5cd/p/532681-color/b/13c013
export const chartPrimaryColors = [
  colors.lavender45, // #505ED9
  colors.teal60, // #04A4B0
  colors.purple60, // #A974F7
  colors.pink45, // #C2306F
  colors.lime60, // #7DA11B - new #7DA11B - old #6BA32A 
  colors.purple45, // #864AE0
  colors.pink60, // #F2638C - new #F2638C - old #E85FC6
  colors.teal45, // #017580
  colors.blue60, // #139BEB
  colors.slate40, // #545C8A
  colors.orange50, // #F26722
  colors.emerald45 // #0B7B46
];

// all colors pass a 3:1 contrast ratio against white.
export const chartPrimaryColorsOld = [
  colors.indigo60, // #7D8AFF
  colors.rose40, // #B02863
  colors.pink60, // #F2638C
  colors.purple40, // #753BCC
  colors.lime60, // #6BA32A
  colors.orange40, // #AD3907
  colors.teal60, // #04A4B0
  colors.cyan40, // #006773
  colors.pink60, // #E85FC6
  colors.lavendar40 // #545C8A
];

export const sentimentColors = {
  neutral: colors.grayUnknown,
  interact: colors.blue45,
  success: colors.greenSuccess,
  warning: colors.yellowFair,
  danger: colors.redPoor,
  info: colors.blue50
};
 
export const statusColors = {
  good: colors.greenSuccess,
  fair: colors.yellowFair,
  poor: colors.redPoor,
  gray: colors.grayUnknown,
  black: colors.gray0,
  danger: colors.redPoor
};

export const meanQuality = [
  "#81A68E", // color for 91%
  "#8C888B", // color for 81%
  "#8C8788", // color for 73%
  "#8C7479", // color for 64%
  "#A67777", // color for 51%
  "#A68686", // color for 42%
  "#8C4F4F", // color for 32%
  "#A64141" // color for 10%
]

// Color harbor themes
export const hbrThemes = {
  quality: { 
    good: colors.greenSuccess,
    fair: colors.yellowFair,
    poor: colors.redPoor,
    notAvailable: colors.grayUnknown,
    gray: colors.grayUnknown,
    unknown: colors.grayUnknown,
  },
  overlay: {
    used: "var(--hbr-color-indigo-60, #7D8AFF)",
    previous: "var(--hbr-color-rose-40, #B02863)",
    available: "var(--hbr-color-gray-90, #E1E4E8)"
  },
  change: {
    positive: colors.greenSuccess,
    negative: colors.redPoor,
  },
  changeQuality: {
    positive: colors.greenSuccess,
    negative: colors.redPoor,
  }
};

export const hbrTextThemes = {
  quality: textStatusColors,
  change: textChangeColors,
  changeQuality: textChangeColors,
};

// Get value color from theme
export const getColor = (value, theme) => {
  switch (theme) {
    case "qoe": {
      const qoeScore = getQoeScore(value);
      return getQualityColor(qoeScore);
    }
    case "availability": {
      const availScore = getAvailabilityScore(value);
      return getQualityColor(availScore);
    }
    case "change":
    case "changeQuality":
      return getChangeColor(value, theme);
    default:
      return defaultColor;
  }
};

// Get text-safe value color from theme.
export const getTextColor = (value, theme) => {
  switch (theme) {
    case "qoe": {
      const qoeScore = getQoeScore(value);
      return getTextQualityColor(qoeScore);
    }
    case "availability": {
      const availScore = getAvailabilityScore(value);
      return getTextQualityColor(availScore);
    }
    case "change":
    case "changeQuality":
      return getTextChangeColor(value, theme);
    default:
      return defaultColor;
  }
};

// Return color for positive or negative value
export const getChangeColor = (value, type) => {
  const theme = hbrThemes[type];
  if (value === 0) return defaultColor;
  return value > -0.5 ? theme.positive : theme.negative;
};

export const getTextChangeColor = (value, type) => {
  const theme = hbrTextThemes[type];
  if (value === 0) return defaultColor;
  return value > -0.5 ? theme.positive : theme.negative;
};

// Return quality color if value is within quality range
export const getQualityColor = score => {
  const theme = hbrThemes.quality;
  return theme[score] ? theme[score] : theme.unknown;
};
export const getTextQualityColor = score => {
  const theme = hbrTextThemes.quality;
  return theme[score] ? theme[score] : theme.unknown;
};
// Return quality color if value is within quality range
export const getQualityColorHbr = score => {
  const theme = hbrThemes.quality;
  return theme[score] ? theme[score] : defaultColor;
};

// Return colors from a theme as array
export const getThemeColors = theme => {
  const colors = hbrThemes[theme];
  if (!colors) return [];
  return Object.values(colors);
};

export const anomalyColors = {
  loss: colors.teal60,
  latency: colors.purple40,
  jitter: colors.lime60,
  Baseline: colors.gray80,
  Usage: colors.orange40
}
